import { NgClass } from '@angular/common';
import { Component, computed, inject, input, signal } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { TNil } from '@app/app.interface';
import { DeliverableListComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.component';
import { TDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { DeliverableListService } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.service';
import { EExecutionType } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { getTimeFrameLabel } from '@utilities/execution-time-frame.util';
import { SqlToAnalyticsUtilitiesService } from '@utilities/sql-to-analytics-utilities.service';
import { ICtaAnalyticsData } from '../../cta-analytics-table.interface';
import { CtaAnalyticsDetailsKpiGoalsTableComponent } from '../cta-analytics-details-kpi-goals-table/cta-analytics-details-kpi-goals-table.component';
import { IKpiCard } from '../cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';

@Component({
  imports: [
    NgClass,
    CtaAnalyticsDetailsKpiGoalsTableComponent,
    DeliverableListComponent,
    IconComponent,
    MatButtonModule,
    TranslateModule,
  ],
  selector: 'app-cta-analytics-details-planned-view',
  templateUrl: './cta-analytics-details-planned-view.component.html',
})
export class CtaAnalyticsDetailsPlannedViewComponent {
  public readonly analyticsNode = input.required<IAnalyticsNode>();
  public readonly execution = input.required<ICtaAnalyticsData>();
  public readonly kpiGoalsKpiCards = input<IKpiCard[]>([]);
  public readonly creatorId = input<string | TNil>(null);
  public readonly creatorBriefingMode = input<boolean>(false);

  public readonly isAllExpanded = signal<boolean>(true);

  public readonly EExecutionType = EExecutionType;

  public readonly plannedExecutionType = computed<EExecutionType | null>(
    () => this.analyticsNode().plannedExecution?.executionType ?? null,
  );

  public readonly timeFrameLabel = computed<string>(() => {
    const { plannedExecution } = this.analyticsNode();

    return plannedExecution?.executionType === EExecutionType.timeFrame
      ? getTimeFrameLabel(plannedExecution, null, this.translate, this.dateUtils)
      : '';
  });

  public readonly plannedDeliverableFormArray = computed<FormArray<FormGroup<TDeliverableForm>>>(() => {
    const formArray = new FormArray<FormGroup<TDeliverableForm>>([]);
    const { emptyCtaAnalytics } = this.analyticsNode();

    if (!emptyCtaAnalytics) {
      return formArray;
    }

    const deliverables = this.sqlToAnalyticsUtilitiesService.getDeliverablesInSqlFormat(emptyCtaAnalytics);

    for (const deliverable of deliverables) {
      const form = this.deliverableListService.createDeliverable(
        deliverable.type,
        deliverable,
        this.creatorId() ?? undefined,
        true,
      );

      if (form) {
        formArray.push(form);
      }
    }

    return formArray;
  });

  private readonly sqlToAnalyticsUtilitiesService = inject(SqlToAnalyticsUtilitiesService);
  private readonly deliverableListService = inject(DeliverableListService);
  private readonly translate = inject(TranslateService);
  private readonly dateUtils = inject(DateUtilitiesService);

  public toggleExpand(): void {
    this.isAllExpanded.set(!this.isAllExpanded());
  }
}
